import { useCallback, useState } from 'react';
import type { ViewMode } from './CatalogViewToggle';

const isViewMode = (value: unknown): value is ViewMode =>
  value === 'cards' || value === 'table';

function readStoredViewMode(storageKey: string): ViewMode | null {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return isViewMode(stored) ? stored : null;
  } catch {
    // Storage unavailable (privacy mode, disabled cookies) — use the default.
    return null;
  }
}

/**
 * IDP-1291: card/table view mode that survives page reloads.
 * Starts in `defaultMode` on first visit; afterwards the user's last choice is
 * read from localStorage under `storageKey`.
 */
export function usePersistentViewMode(
  storageKey: string,
  defaultMode: ViewMode = 'cards',
): [ViewMode, (mode: ViewMode) => void] {
  const [viewMode, setViewModeState] = useState<ViewMode>(
    () => readStoredViewMode(storageKey) ?? defaultMode,
  );

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeState(mode);
      try {
        window.localStorage.setItem(storageKey, mode);
      } catch {
        // Not persisted, but the toggle still works for this visit.
      }
    },
    [storageKey],
  );

  return [viewMode, setViewMode];
}
